import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Siren } from "lucide-react";
import { Chip, Empty, Field, Loading, NSelect, Panel, Plate, useFetch } from "@/components/common";
import { CapSlot, Thumbs } from "@/components/CapSlot";
import { useDraft } from "@/components/Photos";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, fDate, fNum } from "@/lib/api";

const LIC_CLS = { a_controler: "bg-amber-50 text-amber-700 border-amber-200", valide: "bg-emerald-50 text-emerald-700 border-emerald-200", a_renouveler: "bg-orange-50 text-orange-700 border-orange-200", expire: "bg-rose-50 text-rose-700 border-rose-200" };
export const LicChip = ({ d }) => <Chip testid="licence-status" cls={LIC_CLS[d.control_status]}>{d.control_label}</Chip>;

export function MyLicence() {
  const { data, reload } = useFetch("/m2/licence");
  const [recto, setRecto] = useState([]);
  const [verso, setVerso] = useState([]);
  const [cats, setCats] = useState("B");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/m2/licence", { recto_file_id: recto[0], verso_file_id: verso[0] || null, categories: cats.split(/[,\s]+/) });
      toast.success("Permis déposé : à contrôler par la Direction");
      setRecto([]); setVerso([]); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const c = data?.current;
  return (
    <div className="space-y-4" data-testid="my-licence">
      {c ? (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2" data-testid="my-licence-current">
          <div className="flex flex-wrap items-center gap-2"><b className="text-[#0E2D43]">Permis actuel</b><LicChip d={c} /><span className="text-xs text-slate-500">déposé le {fDate(c.created_at)} par {c.added_by_name}{c.checked_date ? ` · contrôlé le ${fDate(c.checked_date)}` : ""}</span></div>
          <div className="text-sm">Catégorie(s) : {c.categories?.join(", ") || "—"}</div>
          <Thumbs ids={[c.recto_file_id, c.verso_file_id].filter(Boolean)} testid="my-licence-thumbs" />
        </div>
      ) : <Empty text="Aucun permis déposé" />}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <b className="text-[#0E2D43]">{c ? "Remplacer mon permis" : "Déposer mon permis"}</b>
        <div className="flex flex-wrap gap-6">
          <CapSlot label="Recto" ids={recto} onChange={setRecto} single required testid="licence-recto" />
          <CapSlot label="Verso" ids={verso} onChange={setVerso} single testid="licence-verso" />
        </div>
        <Field label="Catégorie(s) de permis"><Input value={cats} onChange={(e) => setCats(e.target.value)} className="h-11" data-testid="licence-categories" /></Field>
        <Button disabled={!recto.length || busy} onClick={send} className="w-full h-12 bg-[#0E2D43]" data-testid="licence-submit">Envoyer mon permis</Button>
      </div>
    </div>
  );
}

const REQ = ["avant", "arriere", "gauche", "droit"];
export const INSP_DRAFT = "draft_inspection";
const SIN_DRAFT = "draft_sinistre";


export function NewInspection({ slots, reasons, onDone }) {
  const [dr, setDr, clear] = useDraft(INSP_DRAFT, { photos: {}, reason: "premier", km: "", dmg: [] });
  const { photos, reason, km, dmg } = dr;
  const up = (k) => (val) => setDr((s) => ({ ...s, [k]: typeof val === "function" ? val(s[k]) : val }));
  const setReason = up("reason");
  const setKm = up("km");
  const setDmg = up("dmg");
  const [busy, setBusy] = useState(false);
  const ok = REQ.every((k) => photos[k]?.length) && dmg.every((d) => d.zone && d.description);
  const send = async () => {
    setBusy(true);
    try {
      const d = new Date();
      await api.post("/m2/inspections", { reason, mileage: km ? Number(km) : null, photos, damages: dmg, time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` });
      clear();
      toast.success("État des lieux enregistré (verrouillé)");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const setD = (i, k, v) => setDmg((cur) => cur.map((d, j) => (j === i ? { ...d, [k]: v } : d)));
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4" data-testid="new-inspection">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Motif"><NSelect value={reason} onChange={setReason} testid="insp-reason" options={Object.entries(reasons).map(([value, label]) => ({ value, label }))} /></Field>
        <Field label="Kilométrage (si disponible)"><Input inputMode="numeric" value={km} onChange={(e) => setKm(e.target.value)} className="h-11" data-testid="insp-mileage" /></Field>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">{Object.entries(slots).map(([k, l]) => (
        <CapSlot key={k} label={l} ids={photos[k] || []} onChange={(ids) => up("photos")((cur) => ({ ...cur, [k]: ids }))} required={REQ.includes(k)} testid={`insp-slot-${k}`} />
      ))}</div>
      <div className="space-y-3">
        <div className="flex items-center justify-between"><b className="text-[#0E2D43]">Dommages déjà constatés</b><Button size="sm" variant="outline" onClick={() => setDmg((cur) => [...cur, { zone: "", description: "", photos: [] }])} data-testid="insp-add-damage">Ajouter un dommage</Button></div>
        {dmg.map((d, i) => (
          <div key={i} className="border rounded-lg p-3 grid sm:grid-cols-3 gap-3" data-testid={`insp-damage-${i}`}>
            <Input placeholder="Zone (ex. pare-choc avant)" value={d.zone} onChange={(e) => setD(i, "zone", e.target.value)} data-testid={`insp-damage-zone-${i}`} />
            <Input placeholder="Description" value={d.description} onChange={(e) => setD(i, "description", e.target.value)} data-testid={`insp-damage-desc-${i}`} />
            <CapSlot label="Photo(s)" ids={d.photos} onChange={(ids) => setD(i, "photos", ids)} testid={`insp-damage-photo-${i}`} />
          </div>
        ))}
      </div>
      <p className="text-xs text-emerald-700" data-testid="insp-draft-note">Chaque photo est sauvegardée automatiquement : si la page se recharge, vous reprenez où vous en étiez.</p>
      <p className="text-xs text-slate-500">Une fois validé, l'état des lieux est verrouillé. Un nouvel état des lieux crée une nouvelle version et conserve les précédentes.</p>
      <Button disabled={!ok || busy} onClick={send} className="w-full h-12 bg-[#F49619] hover:bg-[#E0830B] text-white" data-testid="insp-submit">Valider l'état des lieux</Button>
    </div>
  );
}

export function InspectionView({ i, slots, testid }) {
  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2 text-sm"><b>Version {i.version}</b><Plate>{i.plate}</Plate><span>{i.reason_label}</span><span className="text-slate-500">{fDate(i.date)} {i.time || ""} · {i.user_name}{i.mileage ? ` · ${fNum(i.mileage)} km` : ""}</span></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{Object.entries(slots).map(([k, l]) => i.photos?.[k]?.length ? <div key={k}><div className="text-[11px] font-semibold text-slate-500 mb-1">{l}</div><Thumbs ids={i.photos[k]} /></div> : null)}</div>
      {i.damages?.length > 0 && <div className="text-sm"><b>Dommages déjà constatés :</b>{i.damages.map((d, j) => <div key={j} className="mt-1">• {d.zone} — {d.description} <Thumbs ids={d.photos} /></div>)}</div>}
    </div>
  );
}

const RESP = [["oui", "Oui"], ["non", "Non"], ["a_determiner", "À déterminer"]];
const YN = [[true, "Oui"], [false, "Non"]];
const CATS = [["vue_generale", "Vue générale"], ["degats_prest", "Dégâts véhicule Prest'hyg"], ["degats_tiers", "Dégâts véhicule tiers"], ["environnement", "Environnement / lieu"], ["autre", "Autres"]];

function Choice({ label, value, onChange, opts, testid }) {
  return (
    <Field label={label}>
      <div className="flex flex-wrap gap-2" data-testid={testid}>{opts.map(([v, l]) => (
        <button key={String(v)} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${v}`} className={`px-4 h-10 rounded-lg border text-sm font-semibold transition-colors ${value === v ? "bg-[#0E2D43] text-white border-[#0E2D43]" : "bg-white text-slate-600 border-slate-200"}`}>{l}</button>
      ))}</div>
    </Field>
  );
}

export function DeclareSinistre({ vehicle, user, onDone }) {
  const d0 = new Date();
  const p = (n) => String(n).padStart(2, "0");
  const [dr, setDr, clear] = useDraft(SIN_DRAFT, { ph: {}, f: { date: `${d0.getFullYear()}-${p(d0.getMonth() + 1)}-${p(d0.getDate())}`, time: `${p(d0.getHours())}:${p(d0.getMinutes())}`, location: "", description: "", third_party: null, responsible: null, drivable: null, amicable: null, police: null, repair: null, comment: "" } });
  const { f, ph } = dr;
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => { const val = v?.target ? v.target.value : v; setDr((s) => ({ ...s, f: { ...s.f, [k]: val } })); };
  const ok = f.location.trim() && f.description.trim() && ["third_party", "responsible", "drivable", "amicable", "police", "repair"].every((k) => f[k] !== null);
  const send = async () => {
    setBusy(true);
    try {
      const photos = Object.entries(ph).flatMap(([c, ids]) => ids.map((file_id) => ({ file_id, category: c })));
      const { data } = await api.post("/m2/sinistres", { ...f, photos });
      clear();
      toast.success(`Sinistre ${data.number} déclaré — la Direction est prévenue`);
      onDone(data.id);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="declare-sinistre">
      <div className="flex flex-wrap items-center gap-2 text-sm"><b>{user.name}</b>{vehicle && <Plate>{vehicle.plate}</Plate>}</div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date *"><Input type="date" value={f.date} onChange={set("date")} className="h-11" data-testid="sin-date" /></Field>
        <Field label="Heure *"><Input type="time" value={f.time} onChange={set("time")} className="h-11" data-testid="sin-time" /></Field>
      </div>
      <Field label="Lieu *"><Input value={f.location} onChange={set("location")} className="h-11" data-testid="sin-location" /></Field>
      <Field label="Description courte des faits *"><Input value={f.description} onChange={set("description")} className="h-11" data-testid="sin-description" /></Field>
      <Choice label="Tiers impliqué *" value={f.third_party} onChange={set("third_party")} opts={YN} testid="sin-third" />
      <Choice label="Responsabilité *" value={f.responsible} onChange={set("responsible")} opts={RESP} testid="sin-resp" />
      <Choice label="Véhicule encore roulant *" value={f.drivable} onChange={set("drivable")} opts={YN} testid="sin-drivable" />
      <Choice label="Constat amiable réalisé *" value={f.amicable} onChange={set("amicable")} opts={YN} testid="sin-amicable" />
      <Choice label="Forces de l'ordre intervenues *" value={f.police} onChange={set("police")} opts={YN} testid="sin-police" />
      <Choice label="Réparation nécessaire *" value={f.repair} onChange={set("repair")} opts={RESP} testid="sin-repair" />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">{CATS.map(([c, l]) => <CapSlot key={c} label={l} ids={ph[c] || []} onChange={(ids) => setDr((s) => ({ ...s, ph: { ...s.ph, [c]: ids } }))} testid={`sin-photo-${c}`} />)}</div>
      <Field label="Commentaire complémentaire"><Input value={f.comment} onChange={set("comment")} className="h-11" data-testid="sin-comment" /></Field>
      {f.drivable === false && <div className="rounded-lg bg-rose-600 text-white font-bold p-3 text-sm" data-testid="sin-immob-alert">Véhicule non roulant : il sera automatiquement passé au statut « Immobilisé ».</div>}
      <Button disabled={!ok || busy} onClick={send} className="w-full h-12 bg-rose-600 hover:bg-rose-700 text-white font-semibold" data-testid="sin-submit"><Siren className="w-4 h-4 mr-1" /> Envoyer la déclaration</Button>
    </div>
  );
}

export function SinistreList({ rows, admin }) {
  if (!rows?.length) return <Empty text="Aucun sinistre déclaré" />;
  return (
    <div className="divide-y" data-testid="sinistre-list">{rows.map((a) => (
      <Link key={a.id} to={admin ? `/livraison/accidents/${a.id}` : `/commerciaux/sinistres/${a.id}`} className="flex flex-wrap items-center gap-2 py-2 text-sm hover:bg-slate-50 px-2" data-testid={`sinistre-row-${a.id}`}>
        <b>{a.number}</b><span>{fDate(a.date)}</span><Plate>{a.plate}</Plate><span className="text-slate-500">{a.driver_name}</span><Chip>{a.status_label}</Chip>
      </Link>
    ))}</div>
  );
}

export function ComSpaceAdmin({ userId }) {
  const lic = useFetch("/m2/licence", { user_id: userId });
  const ins = useFetch("/m2/inspections", { user_id: userId });
  const sin = useFetch("/m2/sinistres", { user_id: userId });
  const [cmp, setCmp] = useState([]);
  const c = lic.data?.current;
  const control = async (status) => {
    try { await api.patch(`/m2/licence/${c.id}/control`, { status }); toast.success("Statut du permis mis à jour"); lic.reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const list = ins.data?.inspections || [];
  return (
    <div className="space-y-6 mt-6" data-testid="com-space-admin">
      <Panel title="Permis de conduire (dépôt du salarié)" testid="admin-licence">
        {!c ? <Empty text="Aucun permis déposé" /> : (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2"><LicChip d={c} /><span className="text-xs text-slate-500">déposé le {fDate(c.created_at)} par {c.added_by_name} · catégorie(s) {c.categories?.join(", ") || "—"}</span></div>
            <Thumbs ids={[c.recto_file_id, c.verso_file_id].filter(Boolean)} testid="admin-licence-thumbs" />
            <div className="w-64"><NSelect value={c.control_status} onChange={control} testid="admin-licence-status" options={Object.entries(lic.data.statuses).map(([value, label]) => ({ value, label }))} /></div>
          </div>
        )}
      </Panel>
      <Panel title="États des lieux (toutes versions)" testid="admin-inspections">
        {!list.length ? <Empty text="Aucun état des lieux" /> : (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Cochez deux versions pour les comparer côte à côte (ex. restitution vs nouvelle affectation).</p>
            {list.map((i) => <label key={i.id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={cmp.includes(i.id)} onChange={() => setCmp(cmp.includes(i.id) ? cmp.filter((x) => x !== i.id) : [...cmp, i.id].slice(-2))} data-testid={`cmp-${i.id}`} /> V{i.version} · {i.plate} · {i.reason_label} · {fDate(i.date)} · {i.user_name}</label>)}
            <div className={`grid gap-4 ${cmp.length === 2 ? "lg:grid-cols-2" : ""}`}>{(cmp.length ? list.filter((i) => cmp.includes(i.id)) : list.slice(0, 1)).map((i) => <div key={i.id} className="border rounded-lg p-3"><InspectionView i={i} slots={ins.data.slots} testid={`admin-insp-${i.id}`} /></div>)}</div>
          </div>
        )}
      </Panel>
      <Panel title="Sinistres déclarés" testid="admin-sinistres"><SinistreList rows={sin.data} admin /></Panel>
      {(lic.loading || ins.loading) && <Loading />}
    </div>
  );
}
